import { useRef, useState } from "react";
import type { SceneState } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState, useEditorStore, useUi } from "../../../../state/editor/useEditor";
import { pickFile } from "../../../../util/download";
import { basicEngineFor } from "../applyEngine";
import { useManifest } from "../../library/useManifest";
import { AddTile, PresetGrid, PresetTile } from "../../library/PresetTile";
import { LIGHT_BALL } from "../../library/sphereArt";
import { Note, Section } from "./ui";

/**
 * Basic's two lighting libraries, the reference's "Metal HDR Presets" and
 * "Gem HDR Presets" — the content of Advanced's Environment and Gem light
 * panels (same presets, same libraries, same RPCs, and Metal HDR shares their
 * `ui.lastApplied` keys so both surfaces ring the same tile), drawn as the
 * preset grid and worded for a jeweller.
 *
 * Every tile here is a multi-second load (an HDR fetch, decode and prefilter),
 * so the grid holds a spinner on the tile and locks the rest until it lands,
 * and only then moves the ring.
 */

interface HdriEntry { id: string; label: string; file: string; thumb: string }

const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "That light didn't load.");

/** Which file types "Use your own" accepts. */
const LIGHT_FILES = ".hdr,.exr";

/**
 * Run `fn` with `key` marked busy; one load at a time. The `picked` flavour
 * starts the busy state only once a file is chosen — pickFile can resolve
 * "cancelled" off the window regaining focus while a picked file is still
 * decoding, and clearing the spinner there would unlock the grid mid-load.
 */
function useLoad() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };
  const pick = (key: string, load: (f: File) => Promise<unknown>) => {
    if (busy) return;
    setError(null);
    void pickFile(LIGHT_FILES, (f) => run(key, () => load(f))).catch((e: unknown) => setError(errText(e)));
  };
  return { busy, error, run, pick };
}

// ─────────────────────────────────────────────────────────────── Metal HDR

export function MetalHdrPanel() {
  const { client, catalogs } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const [ui, patchUi] = useUi();
  const scene = useDomainState("scene") as SceneState | undefined;
  const { items: hdris, urlFor, thumbFor } = useManifest<HdriEntry>("/env/manifest.json", "env");
  const { busy, error, run, pick } = useLoad();

  const activePreset = ui.lastApplied.environment || null;
  const activeHdri = ui.lastApplied.envHdri || null;
  /** A preset and a library light are mutually exclusive; "" means "not this one". */
  const setApplied = (preset: string, hdri: string) =>
    patchUi({ lastApplied: { ...ui.lastApplied, environment: preset, envHdri: hdri } });
  const resync = () => store.refresh("scene");

  return (
    <div data-basic-metal-hdr>
      <p className="mb-4 text-[12px] leading-[17px] text-ed-text-2">
        The studio light around the piece — what the metal reflects and what lights the scene.
      </p>
      {scene?.metalEnvSource === "override" ? (
        <div className="mb-4">
          <Note>The metal is set to reflect its own light in Advanced, so these change the scene's light but not the metal's.</Note>
        </div>
      ) : null}
      <Section label="Studio lights">
        <PresetGrid>
          <AddTile
            label="Use yours"
            title="Light the scene with your own studio light file"
            busy={busy === "own"}
            disabled={!!busy}
            onClick={() => pick("own", async (f) => {
              await r.loadEnvFromFile(f);
              setApplied("", "");
              await resync();
            })}
            data-metal-hdr-add
          />
          {catalogs.envPresets.map((p) => (
            <PresetTile
              key={p.id}
              label={p.label}
              preview={{ backgroundImage: LIGHT_BALL }}
              on={activePreset === p.id}
              busy={busy === `preset:${p.id}`}
              disabled={!!busy}
              onClick={() => void run(`preset:${p.id}`, async () => {
                await r.setEnvironment(p.id);
                setApplied(p.id, "");
                await resync();
              })}
              data-metal-hdr={p.id}
            />
          ))}
          {/* Addressed by the manifest entry, never by `${id}.hdr` — an API
              preset's id is a database id, and a guessed URL 404s. */}
          {hdris.map((h) => (
            <PresetTile
              key={h.id}
              label={h.label}
              preview={h.thumb ? { backgroundImage: `url("${thumbFor(h)}")` } : { backgroundImage: LIGHT_BALL }}
              on={activeHdri === h.id}
              busy={busy === `hdri:${h.id}`}
              disabled={!!busy}
              onClick={() => void run(`hdri:${h.id}`, async () => {
                await r.loadEnvFromUrl(urlFor(h));
                setApplied("", h.id);
                await resync();
              })}
              data-metal-hdr={h.id}
            />
          ))}
        </PresetGrid>
      </Section>
      {error ? <Note tone="error">{error}</Note> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── Gem HDR

/**
 * Session-only, like Advanced's Gem light panel: the viewer has no getter for
 * the stones' light or its turn, and a reload reboots both to the default, so
 * a persisted value would ring a tile that is no longer applied.
 */
const gemLight: { applied: string | null; turn: number } = { applied: null, turn: 0 };

export function GemHdrPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const { items: gems, urlFor, thumbFor } = useManifest<HdriEntry>("/gem-env/manifest.json", "gemEnv");
  const { busy, error, run, pick } = useLoad();
  const [active, setActive] = useState<string | null>(gemLight.applied);
  const [turn, setTurn] = useState(gemLight.turn);
  const dragFrom = useRef<number | null>(null);

  const choose = (id: string | null) => {
    gemLight.applied = id;
    setActive(id);
  };

  /** One drag of "Turn the light" is one Undo step (the engine merges a
   *  gesture's writes under one key). */
  const setTurnTo = (v: number) => {
    const from = dragFrom.current ?? gemLight.turn;
    dragFrom.current = from;
    gemLight.turn = v;
    setTurn(v);
    void r.setDiamondEnvRotation(v);
    const put = (x: number) => async () => {
      gemLight.turn = x;
      setTurn(x);
      await r.setDiamondEnvRotation(x);
    };
    basicEngineFor(store).recordExternal({ label: "Turn the stones' light", key: "basic.gemLightTurn", undo: put(from), redo: put(v) });
  };

  return (
    <div data-basic-gem-hdr>
      <p className="mb-4 text-[12px] leading-[17px] text-ed-text-2">
        The light the stones sparkle with. It is separate from the studio light, so changing one never changes the other.
      </p>
      <Section label="Stone lights">
        <PresetGrid>
          <AddTile
            label="Use yours"
            title="Light the stones with your own studio light file"
            busy={busy === "own"}
            disabled={!!busy}
            onClick={() => pick("own", async (f) => {
              await r.loadDiamondEnvFromFile(f);
              choose(null);
            })}
            data-gem-hdr-add
          />
          {gems.map((g) => (
            <PresetTile
              key={g.id}
              label={g.label}
              preview={g.thumb ? { backgroundImage: `url("${thumbFor(g)}")` } : { backgroundImage: LIGHT_BALL }}
              on={active === g.id}
              busy={busy === g.id}
              disabled={!!busy}
              onClick={() => void run(g.id, async () => {
                await r.loadDiamondEnvFromUrl(urlFor(g));
                choose(g.id);
              })}
              data-gem-hdr={g.id}
            />
          ))}
        </PresetGrid>
      </Section>
      <Section label="Turn the light">
        <input
          type="range"
          className="lux w-full"
          aria-label="Turn the stones' light"
          min={0}
          max={Math.PI * 2}
          step={0.01}
          value={turn}
          onPointerDown={() => { dragFrom.current = gemLight.turn; }}
          onPointerUp={() => { dragFrom.current = null; }}
          onKeyUp={() => { dragFrom.current = null; }}
          onChange={(e) => setTurnTo(parseFloat(e.target.value))}
        />
        <p className="mt-1 text-[12px] leading-[17px] text-ed-text-2">Moves where the sparkle lands on the stones.</p>
      </Section>
      {error ? <Note tone="error">{error}</Note> : null}
    </div>
  );
}
